import { useEffect, useRef, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ArrowRight, ImageIcon, Megaphone, Upload } from "lucide-react";
import {
  BANNER_ROUTES,
  PANEL_BANNER_ROUTES,
  PROOF_MAX_BYTES,
  bannerResponseSchema,
  panelBannerResponseSchema,
  type BannerSettings,
  type BannerSettingsInput,
  type PanelBannerInput,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";
import { cn } from "@/shared/lib/cn";
import { PageHeader } from "@/shared/ui/layout";
import { Alert, Button, Checkbox, Input } from "@/shared/ui/core";
import { SkeletonCard } from "@/shared/ui/ui";
import { AdminGuard } from "@/features/admin/components";
import { fileToBase64 } from "@/features/kyc/kyc.api";

const NO_PREFIX = { baseUrl: "" } as const;

const IMAGE_MIMES = new Set(["image/jpeg", "image/png", "image/webp"]);

async function getBanner(): Promise<BannerSettings> {
  const data = await authFetch(BANNER_ROUTES.admin, bannerResponseSchema, NO_PREFIX);
  return data.banner;
}

async function saveBanner(input: BannerSettingsInput): Promise<BannerSettings> {
  const data = await authFetch(BANNER_ROUTES.admin, bannerResponseSchema, {
    ...NO_PREFIX,
    method: "PUT",
    body: input,
  });
  return data.banner;
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * Banner — DOS banners en una pantalla, cada uno en su pestaña
 * (Anahí, 27-ago):
 *
 *  · TIENDA — la franja de anuncio de texto sobre el shop.
 *  · PANEL — la IMAGEN de campaña que corona los dashboards de socio
 *    y franquiciatario ("El capítulo más importante está por
 *    comenzar"), subida desde aquí.
 *
 * En las dos, la vista previa pinta EXACTAMENTE lo que se enseña.
 * ═══════════════════════════════════════════════════════════════════
 */
export function AdminBannerPage() {
  return (
    <AdminGuard>
      <BannerScreen />
    </AdminGuard>
  );
}

function BannerScreen() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<"tienda" | "panel">("tienda");

  return (
    <>
      <PageHeader
        titleHidden
        title={t("Banner")}
        description={t("El anuncio de la tienda y la imagen de los dashboards del panel.")}
      />

      <div className="rounded-3xl border border-ds-border bg-white shadow-ds-float">
        {/* ── Pestañas integradas al borde, como en Franquicias ───────── */}
        <div role="tablist" className="flex gap-1 border-b border-ds-border px-4 pt-2">
          {(
            [
              { key: "tienda", label: t("Banner de la tienda") },
              { key: "panel", label: t("Banner del panel") },
            ] as const
          ).map(({ key, label }) => (
            <button
              key={key}
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              className={cn(
                "-mb-px border-b-2 px-4 py-2.5 text-[13.5px] font-semibold transition",
                tab === key
                  ? "border-ds-violet-pop text-ds-ink"
                  : "border-transparent text-ds-ink-3 hover:text-ds-ink",
              )}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="p-5">{tab === "tienda" ? <ShopBannerTab /> : <PanelBannerTab />}</div>
      </div>
    </>
  );
}

/** La franja de texto de la tienda — lo que ya existía, ahora en su tab. */
function ShopBannerTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const banner = useQuery({ queryKey: ["admin", "banner"], queryFn: getBanner });

  const [form, setForm] = useState<BannerSettingsInput | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (banner.data && !form) {
      const { updatedAt: _at, ...input } = banner.data;
      setForm(input);
    }
  }, [banner.data, form]);

  const guardar = useMutation({
    mutationFn: saveBanner,
    onSuccess: (fresh) => {
      queryClient.setQueryData(["admin", "banner"], fresh);
      setSaved(true);
    },
    onError: (cause) =>
      setError(cause instanceof Error ? cause.message : t("No se pudo guardar")),
  });

  function enviar(event: FormEvent) {
    event.preventDefault();
    if (!form) return;
    setError(null);
    setSaved(false);
    guardar.mutate({
      enabled: form.enabled,
      title: form.title,
      ...(form.message?.trim() ? { message: form.message.trim() } : {}),
      ...(form.ctaLabel?.trim() ? { ctaLabel: form.ctaLabel.trim() } : {}),
      ...(form.ctaTo?.trim() ? { ctaTo: form.ctaTo.trim() } : {}),
    });
  }

  if (banner.isPending || !form) return <SkeletonCard />;

  return (
    <div className="grid gap-4">
      {form.enabled && form.title.trim() && (
        <AnnouncementBarPreview
          title={form.title}
          message={form.message}
          ctaLabel={form.ctaLabel}
        />
      )}

      <form onSubmit={enviar} className="grid gap-4">
        <Checkbox
          label={t("Banner encendido")}
          checked={form.enabled}
          onChange={(event) => setForm({ ...form, enabled: event.target.checked })}
        />
        <Input
          label={t("Título")}
          value={form.title}
          onChange={(event) => setForm({ ...form, title: event.target.value })}
          placeholder={t("Envío gratis todo agosto")}
          maxLength={80}
        />
        <Input
          label={t("Mensaje (opcional)")}
          value={form.message ?? ""}
          onChange={(event) => setForm({ ...form, message: event.target.value })}
          placeholder={t("En pedidos desde $1,500")}
          maxLength={200}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={t("Texto del botón (opcional)")}
            value={form.ctaLabel ?? ""}
            onChange={(event) => setForm({ ...form, ctaLabel: event.target.value })}
            placeholder={t("Ver paquetes")}
            maxLength={40}
          />
          <Input
            label={t("A dónde lleva")}
            value={form.ctaTo ?? ""}
            onChange={(event) => setForm({ ...form, ctaTo: event.target.value })}
            placeholder="/paquetes"
            maxLength={200}
          />
        </div>

        {error && (
          <Alert variant="error" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}
        {saved && (
          <Alert variant="success" onDismiss={() => setSaved(false)}>
            {t("Guardado. La tienda ya lo enseña (o lo esconde).")}
          </Alert>
        )}

        <div className="flex justify-end">
          <Button type="submit" loading={guardar.isPending}>
            {t("Guardar cambios")}
          </Button>
        </div>
      </form>
    </div>
  );
}

/**
 * La IMAGEN de los dashboards: se sube aquí (foto ≤4 MB, viaja como
 * dataUrl) y los paneles de socio y franquiciatario la coronan.
 */
function PanelBannerTab() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const banner = useQuery({
    queryKey: ["admin", "panel-banner"],
    queryFn: async () => {
      const data = await authFetch(PANEL_BANNER_ROUTES.admin, panelBannerResponseSchema, NO_PREFIX);
      return data.banner;
    },
  });

  const [form, setForm] = useState<PanelBannerInput | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (banner.data && !form) {
      const { updatedAt: _at, ...input } = banner.data;
      setForm(input);
    }
  }, [banner.data, form]);

  const guardar = useMutation({
    mutationFn: async (input: PanelBannerInput) => {
      const data = await authFetch(PANEL_BANNER_ROUTES.admin, panelBannerResponseSchema, {
        ...NO_PREFIX,
        method: "PUT",
        body: input,
      });
      return data.banner;
    },
    onSuccess: (fresh) => {
      queryClient.setQueryData(["admin", "panel-banner"], fresh);
      // Los dashboards leen su propia consulta: que se entere ya.
      void queryClient.invalidateQueries({ queryKey: ["panel-banner"] });
      setSaved(true);
    },
    onError: (cause) =>
      setError(cause instanceof Error ? cause.message : t("No se pudo guardar")),
  });

  async function onFile(file: File | undefined) {
    if (!file || !form) return;
    setError(null);
    if (!IMAGE_MIMES.has(file.type)) {
      setError(t("Solo imágenes: JPG, PNG o WebP."));
      return;
    }
    if (file.size > PROOF_MAX_BYTES) {
      setError(t("La imagen no puede pasar de 4 MB."));
      return;
    }
    const dataUrl = `data:${file.type};base64,${await fileToBase64(file)}`;
    // Subir una imagen ENCIENDE el banner (Anahí, 27-ago: "se subió y
    // no aparece"): quien sube una imagen quiere verla — olvidar marcar
    // "encendido" dejaba el banner cargado pero invisible.
    setForm({ ...form, imageUrl: dataUrl, enabled: true });
  }

  if (banner.isPending || !form) return <SkeletonCard />;

  return (
    <div className="grid gap-4">
      {/* ── La vista previa: la MISMA card que pintan los dashboards ── */}
      {form.imageUrl ? (
        <img
          src={form.imageUrl}
          alt={form.alt || t("Banner del panel")}
          className="max-h-[320px] w-full rounded-[28px] object-cover shadow-ds-float"
        />
      ) : (
        <div className="flex h-40 flex-col items-center justify-center gap-2 rounded-[28px] border-2 border-dashed border-ds-border bg-ds-mist/30 text-ds-ink-3">
          <ImageIcon className="h-6 w-6" />
          <p className="text-[13px]">{t("Sube la imagen de campaña de los dashboards.")}</p>
        </div>
      )}

      <div className="grid gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(event) => {
              void onFile(event.target.files?.[0]);
              event.target.value = "";
            }}
          />
          <Button
            variant="secondary"
            leftIcon={<Upload />}
            onClick={() => inputRef.current?.click()}
          >
            {form.imageUrl ? t("Cambiar imagen") : t("Subir imagen")}
          </Button>
          <Checkbox
            label={t("Banner encendido")}
            checked={form.enabled}
            onChange={(event) => setForm({ ...form, enabled: event.target.checked })}
          />
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label={t("Qué dice la imagen (accesibilidad)")}
            value={form.alt}
            onChange={(event) => setForm({ ...form, alt: event.target.value })}
            placeholder={t("El capítulo más importante está por comenzar")}
            maxLength={160}
          />
          <Input
            label={t("A dónde lleva el clic (opcional)")}
            value={form.linkTo}
            onChange={(event) => setForm({ ...form, linkTo: event.target.value })}
            placeholder="/paquetes"
            maxLength={200}
          />
        </div>

        {error && (
          <Alert variant="error" onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}
        {saved && (
          <Alert variant="success" onDismiss={() => setSaved(false)}>
            {t("Guardado. Los dashboards de socio y franquiciatario ya lo enseñan.")}
          </Alert>
        )}

        <div className="flex justify-end">
          <Button
            loading={guardar.isPending}
            onClick={() => {
              setError(null);
              setSaved(false);
              guardar.mutate(form);
            }}
          >
            {t("Guardar cambios")}
          </Button>
        </div>
      </div>
    </div>
  );
}

/** La misma franja que pinta la tienda, aquí como vista previa. */
function AnnouncementBarPreview({
  title,
  message,
  ctaLabel,
}: {
  title: string;
  message?: string | undefined;
  ctaLabel?: string | undefined;
}) {
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 rounded-2xl bg-ds-violet-pop px-4 py-2.5 text-center text-[13px] text-white shadow-ds-cta">
      <Megaphone className="h-4 w-4 shrink-0" />
      <span className="font-semibold">{title}</span>
      {message && <span className="text-white/85">{message}</span>}
      {ctaLabel && (
        <span className="inline-flex items-center gap-1 font-semibold underline underline-offset-2">
          {ctaLabel}
          <ArrowRight className="h-3.5 w-3.5" />
        </span>
      )}
    </div>
  );
}
